import { type MouseEvent } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

import { niceCeiling } from './chartScale';

const DEFAULT_HEIGHT = 220;
/** Narrowest a bar column may get before the chart scrolls sideways; keeps labels wrapping by word. */
const MIN_SLOT_WIDTH = 88;
const DEFAULT_TICK_COUNT = 4;

export type VerticalBarChartBar = Readonly<{
  id: string;
  label: string;
  value: number;
  color?: ColorToken;
  /** Shown while the bar is hovered. */
  tooltip?: string;
  onClick?: () => void;
}>;

export type VerticalBarChartProps = Readonly<{
  bars: readonly VerticalBarChartBar[];
  color?: ColorToken;
  /** 1px bar outline. */
  outlineColor?: ColorToken;
  /** Plot height in pixels, excluding the labels below. */
  height?: number;
  tickCount?: number;
  ariaLabel: string;
}>;

/** Vertical bars on a gridded y-axis with wrapping category labels beneath. */
export const VerticalBarChart = ({
  bars,
  color = 'Brand/500',
  outlineColor = 'Colors/Border/border-primary',
  height = DEFAULT_HEIGHT,
  tickCount = DEFAULT_TICK_COUNT,
  ariaLabel,
}: VerticalBarChartProps) => {
  const maxValue = niceCeiling(Math.max(...bars.map((bar) => bar.value), 0), tickCount, 1);
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) =>
    Math.round(maxValue - (maxValue / tickCount) * index),
  );

  return (
    <Box sx={rootSx} role="img" aria-label={ariaLabel}>
      <Box sx={axisSx} style={{ height }}>
        {ticks.map((tick, index) => (
          <Typography key={index} variant="text-xs-regular" sx={tickLabelSx}>
            {tick.toLocaleString()}
          </Typography>
        ))}
      </Box>
      <Box sx={plotColumnSx} style={{ minWidth: bars.length * MIN_SLOT_WIDTH }}>
        <Box sx={plotSx} style={{ height }}>
          {ticks.map((_, index) => (
            <Box key={index} sx={gridLineSx} style={{ top: `${(index / tickCount) * 100}%` }} />
          ))}
          {bars.map((bar) => {
            const pct = maxValue > 0 ? Math.min(bar.value / maxValue, 1) * 100 : 0;
            const column = (
              <Box
                key={bar.id}
                id={bar.id}
                sx={barSlotSx(Boolean(bar.onClick))}
                onClick={
                  bar.onClick &&
                  ((event: MouseEvent) => {
                    event.stopPropagation();
                    bar.onClick?.();
                  })
                }
              >
                <Box
                  sx={barSx}
                  style={{
                    height: `${pct}%`,
                    backgroundColor: COLOR_TOKENS[bar.color ?? color],
                    borderColor: COLOR_TOKENS[outlineColor],
                  }}
                />
              </Box>
            );

            return bar.tooltip ? (
              <Tooltip key={bar.id} arrow placement="top" title={bar.tooltip}>
                {column}
              </Tooltip>
            ) : (
              column
            );
          })}
        </Box>
        <Box sx={labelsSx}>
          {bars.map((bar) => (
            <Typography key={bar.id} variant="text-xs-regular" sx={labelSx}>
              {bar.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
  boxSizing: 'border-box',
  overflowX: 'auto',
  // Without it the plot's minimum width still sizes every ancestor, so the page scrolls instead.
  contain: 'inline-size',
});

const axisSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const tickLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  lineHeight: 1,
  textAlign: 'right',
  transform: 'translateY(-50%)',
  '&:first-of-type': { transform: 'none' },
  '&:last-of-type': { transform: 'translateY(-100%)' },
});

const plotColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const plotSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  boxSizing: 'border-box',
});

const gridLineSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  pointerEvents: 'none',
});

const barSlotSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    flex: 1,
    minWidth: 0,
    height: '100%',
    paddingX: backrs.spacing['spacing-sm'],
    boxSizing: 'border-box',
    cursor: isClickable ? 'pointer' : 'default',
    '&:hover > *': { opacity: 0.85 },
  });

const barSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  maxWidth: 56,
  boxSizing: 'border-box',
  border: '1px solid',
  borderRadius: `${backrs.radius['radius-xs']} ${backrs.radius['radius-xs']} 0 0`,
  transition: 'opacity 120ms ease',
});

const labelsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  paddingTop: backrs.spacing['spacing-sm'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  paddingX: backrs.spacing['spacing-xs'],
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-tertiary'],
  overflowWrap: 'break-word',
  hyphens: 'auto',
});
